import { Scrollable } from 'components/atoms';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { branchIdAtom, branchNameAtom } from 'store/atoms/branchAtom';
import { setStoredBranch } from 'utils/branch';
import {
  CategoryProductRow,
  DesktopCategoryFilter,
  formatCategoryName,
  MobileCategorySidebar,
  MobileCategoryFilter
} from './CategoryComponents';
import HeaderVendor from './header-vendor';
import { CategorySection } from './style';
import { ProductSkeletons } from '../Products';
import useProductsByVendorsCategoriesHook from './useProductsByVendorsCategoriesHook';

interface Product {
  productId: number;
  productName: string;
  price: number;
  quantityAvailable: number;
  minimumOrderQuantity: number;
  bulkOrderPrice: number;
  barCode: string;
  productImage1: string;
  subCategory: string;
  status?: string;
  description?: string;
}

const ProductsByVendor = () => {
  const {
    fetchMoreProductsByVendors,
    productsByVendorsData,
    productsByVendorsisFetching,
    productsByVendorsHasNext,
    seller,
    subCategories,
    isInitialLoading,
    isFetchingNextPage
  } = useProductsByVendorsCategoriesHook() as {
    fetchMoreProductsByVendors: () => void;
    productsByVendorsData: any;
    productsByVendorsisFetching: boolean;
    productsByVendorsHasNext: boolean;
    seller: any;
    subCategories: string[];
    isInitialLoading?: boolean;
    isFetchingNextPage?: boolean;
  };

  const navigate = useNavigate();
  const location = useLocation();
  const setBranchId = useSetRecoilState(branchIdAtom);
  const setBranchName = useSetRecoilState(branchNameAtom);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const locationParam = new URLSearchParams(location.search).get('locationId');
    const id = Number(locationParam);
    if (!Number.isFinite(id) || id <= 0) return;
    const name = seller?.businessName || seller?.name || '';
    setBranchId(id);
    if (name) setBranchName(name);
    setStoredBranch(id, name || undefined);
  }, [location.search, seller?.businessName, seller?.name, setBranchId, setBranchName]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showSidebar, setShowSidebar] = useState(false);

  // Function to handle search query change
  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  // Function to handle back button click
  const handleBack = () => {
    const params = new URLSearchParams(location.search);
    params.delete('vendor');
    navigate({ search: params.toString() });
  };

  // Function to handle category click navigation
  const handleCategoryClick = (categoryEnum: string, prefetchedProducts: Product[] = []) => {
    const params = new URLSearchParams(location.search);
    const vendorParam = params.get('vendor');
    const categoryParam = params.get('category');

    if (vendorParam && categoryParam) {
      const pathSegments = location.pathname.split('/');
      const currentCategoryName = pathSegments[pathSegments.length - 1];
      const currentPath = location.pathname;
      const basePath = currentPath.split('/').slice(0, -1).join('/');

      const locationParam = params.get('locationId');
      const branchId = locationParam ? Number(locationParam) : undefined;

      navigate(
        `${basePath}/${categoryParam}/${vendorParam}/${encodeURIComponent(categoryEnum)}`,
        {
          state: {
            seller,
            currentCategory: currentCategoryName,
            parentCategoryId: Number(categoryParam),
            prefetchedProducts,
            branchId: Number.isFinite(branchId) && branchId! > 0 ? branchId : undefined
          }
        }
      );
    }
  };

  // Function to handle category selection in sidebar
  const handleCategorySelect = (categoryEnum: string | null) => {
    setSelectedCategory(categoryEnum);
  };

  // Get all products using useMemo for better performance
  const allProducts = useMemo(() => {
    if (!productsByVendorsData?.pages) return [];

    return productsByVendorsData.pages.flatMap((page: any) => {
      if (page?.data && Array.isArray(page.data)) {
        return page.data;
      }
      return [];
    });
  }, [productsByVendorsData?.pages]);

  // Group products by category with better memoization
  const groupedProducts = useMemo(() => {
    const groups: { [key: string]: { categoryEnum: string; products: Product[] } } = {};

    // Initialize groups for each subcategory
    subCategories?.forEach(categoryEnum => {
      groups[categoryEnum] = {
        categoryEnum,
        products: []
      };
    });

    // Group products by their subcategory
    allProducts.forEach((product: Product) => {
      if (product.subCategory) {
        if (groups[product.subCategory]) {
          // Apply search filter
          if (searchQuery.trim()) {
            const searchLower = searchQuery.toLowerCase();
            const matchesSearch =
              product.productName?.toLowerCase().includes(searchLower) ||
              product.barCode?.toLowerCase().includes(searchLower) ||
              product.description?.toLowerCase().includes(searchLower);

            if (matchesSearch) {
              groups[product.subCategory].products.push(product);
            }
          } else {
            groups[product.subCategory].products.push(product);
          }
        } else {
          groups[product.subCategory] = {
            categoryEnum: product.subCategory,
            products: []
          };
          if (searchQuery.trim()) {
            const searchLower = searchQuery.toLowerCase();
            const matchesSearch =
              product.productName?.toLowerCase().includes(searchLower) ||
              product.barCode?.toLowerCase().includes(searchLower) ||
              product.description?.toLowerCase().includes(searchLower);
            if (matchesSearch) {
              groups[product.subCategory].products.push(product);
            }
          } else {
            groups[product.subCategory].products.push(product);
          }
        }
      }
    });

    // Define the fixed order for categories
    const categoryOrder = ['PROVISIONS', 'DRINKS', 'COSMETICS', 'TOILETRIES'];

    // Filter out empty categories and sort by the fixed order
    const result = Object.values(groups)
      .filter(group => group.products.length > 0)
      .sort((a, b) => {
        const aIndex = categoryOrder.indexOf(a.categoryEnum);
        const bIndex = categoryOrder.indexOf(b.categoryEnum);

        // If both categories are in the order array, sort by their position
        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex;
        }

        // If only one is in the order array, prioritize it
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;

        // If neither is in the order array, maintain alphabetical order
        return a.categoryEnum.localeCompare(b.categoryEnum);
      });

    return result;
  }, [allProducts, subCategories, searchQuery]);

  const handleViewAll = (categoryEnum: string) => {
    const group = groupedProducts.find(item => item.categoryEnum === categoryEnum);
    handleCategoryClick(categoryEnum, group?.products ?? []);
  };

  // Filter products based on selected category
  const filteredGroupedProducts = useMemo(() => {
    if (selectedCategory) {
      return groupedProducts.filter(group => group.categoryEnum === selectedCategory);
    }
    return groupedProducts;
  }, [groupedProducts, selectedCategory]);

  // Check if there are no products at all
  const noProducts = allProducts.length === 0;

  const sellerData: any = seller;

  const emptyMessage = noProducts
    ? 'Unfortunately, no products are currently offered by this vendor'
    : searchQuery.trim() && groupedProducts.length === 0
    ? `No products found for "${searchQuery}"`
    : '';

  return (
    <div className="min-h-screen bg-gray-50">
      <HeaderVendor
        title={sellerData?.businessName || 'Loading...'}
        description={sellerData?.businessDescription || ''}
        vendorBannerSrc={sellerData?.seller_logo}
        handleBack={handleBack}
        seller={sellerData}
        isLoading={isInitialLoading && allProducts.length === 0}
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
      />

      <div className="relative">
          {isInitialLoading && allProducts.length === 0 ? (
            <CategorySection className="px-4 lg:px-6 py-8">
              <ProductSkeletons count={8} />
            </CategorySection>
          ) : (
          <>
          {/* Mobile Sidebar Toggle */}
          {/* <div className="lg:hidden fixed top-20 left-4 z-40">
            <button
              onClick={() => setShowSidebar(!showSidebar)}
              className="p-2 bg-white border border-gray-300 rounded-lg shadow-lg">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div> */}

          {/* Mobile Sidebar Overlay */}
          {showSidebar && (
            <div
              className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
              onClick={() => setShowSidebar(false)}
            />
          )}

          {/* Mobile Sidebar */}
          <div
            className={`${
              showSidebar ? 'translate-x-0' : '-translate-x-full'
            } lg:hidden fixed inset-y-0 left-0 z-40 transition-transform duration-300 ease-in-out`}>
            <MobileCategorySidebar
              allCategories={subCategories || []}
              groupedProducts={groupedProducts}
              selectedCategory={selectedCategory}
              onCategorySelect={handleCategorySelect}
              onViewAll={handleViewAll}
            />
          </div>

          {/* Main Content - Full width on desktop */}
          <div className="w-full">
            <Scrollable
              onScrollBottom={fetchMoreProductsByVendors}
              callWhen={productsByVendorsHasNext && !isFetchingNextPage}>
              <CategorySection className="px-4 lg:px-6">
                {noProducts || filteredGroupedProducts.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-gray-600 text-lg">{emptyMessage}</p>
                  </div>
                ) : (
                  <div className="space-y-8">
                    {/* Category Filter Info */}
                    {selectedCategory && (
                      <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">Showing products in:</span>
                          <span className="font-medium text-blue-900">{formatCategoryName(selectedCategory)}</span>
                        </div>
                        <button
                          onClick={() => setSelectedCategory(null)}
                          className="text-sm text-blue-600 hover:text-blue-800 underline">
                          Clear filter
                        </button>
                      </div>
                    )}

                    {filteredGroupedProducts.map(({ categoryEnum, products }) => (
                      <CategoryProductRow
                        key={categoryEnum}
                        categoryEnum={categoryEnum}
                        products={products}
                        seller={sellerData}
                        onCategoryClick={handleCategoryClick}
                        onViewAll={handleViewAll}
                        showViewAll={!selectedCategory}
                      />
                    ))}

                    {/* Loading indicator for infinite scroll */}
                    {/* {(isFetchingNextPage || productsByVendorsisFetching) && (
                      <div className="text-center py-8">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <p className="text-gray-500 mt-2">
                          {isFetchingNextPage ? 'Loading more products...' : 'Loading...'}
                        </p>
                      </div>
                    )} */}
                  </div>
                )}
              </CategorySection>
            </Scrollable>

            {/* Desktop Bottom Category Filter */}
            <div className="hidden lg:block">
              <DesktopCategoryFilter
                allCategories={subCategories || []}
                groupedProducts={groupedProducts}
                selectedCategory={selectedCategory}
                onCategorySelect={handleCategorySelect}
                onViewAll={handleViewAll}
              />
            </div>
            {/* Mobile Browse by Category Filter */}
            <div className="block lg:hidden">
              <MobileCategoryFilter
                allCategories={subCategories || []}
                groupedProducts={groupedProducts}
                selectedCategory={selectedCategory}
                onCategorySelect={handleCategorySelect}
                onViewAll={handleViewAll}
              />
            </div>
          </div>
          </>
          )}
      </div>
    </div>
  );
};

export default ProductsByVendor;
